/**
 * FundReach Pursue API Service (v8.7)
 * Connects to the new agentic /fundreach/pursue/* endpoints.
 *
 * These run alongside (and do not replace) the legacy fundreach.js campaign service.
 */
import apiClient from '../lib/apiClient';

/**
 * Start a new pursuit run for a funder.
 * @param {Object} params
 * @param {string} params.entity_bn - Org charity BN
 * @param {string} params.funder_id - Funder identifier
 * @param {string} params.funder_name - Human-readable funder name
 * @param {string|null} params.grant_id - Specific grant ID (optional)
 * @param {string} params.org_name - Organization name
 * @param {string} params.org_mission - Organization mission statement
 * @param {string[]} params.org_focus_areas - Org program focus areas
 * @returns {Promise<{run_id: string, status: string, poll_url: string}>}
 */
export const startPursuit = async (params) => {
  const response = await apiClient.post('/fundreach/pursue/start', params);
  return response.data;
};

/**
 * Poll pursuit run status and partial results.
 * @param {string} runId
 * @returns {Promise<{run_id, status, phase, hitl_required, structure_result, final_result, error}>}
 */
export const getPursuitStatus = async (runId) => {
  const response = await apiClient.get(`/fundreach/pursue/status/${runId}`);
  return response.data;
};

/**
 * HITL approval — approve structure results and continue (optionally with edits).
 * @param {string} runId
 * @param {Object|null} edits - Optional field edits to patch DIM results
 * @returns {Promise<{run_id, status, edits_applied}>}
 */
export const approvePursuit = async (runId, edits = null) => {
  const response = await apiClient.post(`/fundreach/pursue/approve/${runId}`, { edits });
  return response.data;
};

/**
 * List all pursuit runs for an organization.
 * @param {string} entityBn
 * @param {string|null} status - Filter by status (optional)
 * @returns {Promise<{entity_bn, total, runs: Array}>}
 */
export const listPursuitRuns = async (entityBn, status = null) => {
  const params = status ? { status } : {};
  const response = await apiClient.get(`/fundreach/pursue/runs/${entityBn}`, { params });
  return response.data;
};

/**
 * Cancel a pending or paused pursuit run.
 * @param {string} runId
 */
export const cancelPursuit = async (runId) => {
  await apiClient.delete(`/fundreach/pursue/cancel/${runId}`);
};

/**
 * Trigger pre-run DIM analysis for a funder (admin/system use).
 * @param {string} funderId
 * @param {string} funderName
 */
export const prerunFunderIntelligence = async (funderId, funderName) => {
  const response = await apiClient.post(`/fundreach/pursue/prerun/${funderId}`, null, {
    params: { funder_name: funderName },
  });
  return response.data;
};
